
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import Logger from '@/utils/logger';

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentContextType {
  showBanner: boolean;
  preferences: CookiePreferences;
  consentGiven: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (prefs: CookiePreferences) => void;
  openSettings: () => void;
  closeSettings: () => void;
  showSettings: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const CONSENT_KEY = 'acczen_cookie_consent';
const CONSENT_EXPIRY = 365 * 24 * 60 * 60 * 1000; // 1 year in milliseconds

export const CookieConsentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always required
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const storedConsent = localStorage.getItem(CONSENT_KEY);
    
    if (storedConsent) {
      try {
        const { preferences: storedPrefs, timestamp } = JSON.parse(storedConsent);
        const now = Date.now();
        
        // Check if consent has expired (1 year)
        if (now - timestamp < CONSENT_EXPIRY) {
          setPreferences(storedPrefs);
          setConsentGiven(true);
          setShowBanner(false);
        } else {
          // Consent expired, show banner again
          localStorage.removeItem(CONSENT_KEY);
          setShowBanner(true);
        }
      } catch (error) {
        Logger.error('Error parsing stored consent:', error);
        setShowBanner(true);
      }
    } else {
      // No consent stored, show banner
      setShowBanner(true);
    }
  }, []);

  const saveConsentPreferences = (prefs: CookiePreferences) => {
    const consentData = {
      preferences: prefs,
      timestamp: Date.now(),
    };
    
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
    setPreferences(prefs);
    setConsentGiven(true);
    setShowBanner(false);
    setShowSettings(false);

    // Apply analytics preferences
    if (prefs.analytics && window.gtag) {
      // Enable Google Analytics if analytics cookies are accepted
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
      });
    } else if (window.gtag) {
      // Disable Google Analytics if analytics cookies are rejected
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
      });
    }
  };

  const acceptAll = () => {
    saveConsentPreferences({
      essential: true,
      analytics: true,
      marketing: true,
    });
  };

  const rejectAll = () => {
    saveConsentPreferences({
      essential: true, // Essential always required
      analytics: false,
      marketing: false,
    });
  };

  const savePreferences = (prefs: CookiePreferences) => {
    saveConsentPreferences({ ...prefs, essential: true }); // Ensure essential is always true
  };

  const openSettings = () => {
    setShowSettings(true);
  };

  const closeSettings = () => {
    setShowSettings(false);
  };

  return (
    <CookieConsentContext.Provider
      value={{
        showBanner,
        preferences,
        consentGiven,
        acceptAll,
        rejectAll,
        savePreferences,
        openSettings,
        closeSettings,
        showSettings,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};
